import {scroller} from 'react-scroll';
import {cloneDeep, setWith, unset} from 'lodash';

import I18N from 'assets/i18n/brandlift-project';

import {
    AudienceRequest,
    AudienceType,
    BoxContext,
    BoxType,
    ChoiceQuestion,
    EmailQuestion,
    Question,
    QuestionType,
    Revision,
    SideBySideQuestion,
    TableChoiceQuestion,
    TableScaleQuestion,
    UXContext
} from 'client/common/types';

import {queryClient} from './react-query';
import {extractCleanText} from './markdown';
import { mapSpecialOptions } from './survey';

const i18n = I18N.ux.uxEditor;

const MAX_TASK_LENGTH = 300;

export const SKIP_QUESTION_VALIDATION_BOX_TYPES: BoxType[] = ['ux'];
export const TASK_LABEL_ERROR_KEY = 'task-label';
export const TASK_QUESTIONS_ERROR_KEY = 'task-questions';
export const ERROR_CLASS_NAME = 'error-label';
export const ERRORS_KEY = 'errors';

export interface Errors {
    [key: string]: any;
}

export class ValidationError extends Error {
    constructor(...params: any[]) {
        super(...params);

        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, ValidationError);
        }

        this.name = 'ValidationError';
    }
}

export enum ValidationErrorType {
    Empty = 'EMPTY',
    InvalidDateRange= 'INVALID_DATE_RANGE',
    InvalidDateInput= 'INVALID_DATE_INPUT',
    InvalidDateValue= 'INVALID_DATE_VALUE',
    MaxLengthReached = 'MAX_LENGTH_REACHED',
    InvalidURL = 'INVALID_URL',
    InvalidSize = 'INVALID_SIZE',
    InvalidAudience = 'INVALID_AUDIENCE',
    InvalidQuotas = 'INVALID_QUOTAS',
    EmotionEmptyLabels = 'EMOTION_EMPTY_LABELS',
}

function validateChoiceQuestion(question: ChoiceQuestion): boolean {
    let valid = true;

    question.options.forEach(option => {
        if (!option.attachments?.length && !validateEmptyLabel(option.label, [
            'question',
            question.id,
            `option-${option.id}`
        ])) {
            valid = false;
        }
    });

    return valid;
}

function validateSideBySideQuestion(question: SideBySideQuestion): boolean {
    let valid = true;

    question.options.forEach(option => {
        if (!validateEmptyLabel(option.url, [
            'question',
            question.id,
            `option-${option.id}`
        ])) {
            valid = false;
        }
    });

    return valid;
}

function validateEmailQuestion(question: EmailQuestion, request: AudienceRequest): boolean {
    let valid = true;

    if (request.type !== AudienceType.None) {
        setError([
            'question',
            question.id,
            'input'
        ], ValidationErrorType.InvalidAudience);
        valid = false;
    }

    return valid;
}

function validateTableChoiceQuestion(question: TableChoiceQuestion): boolean {
    let valid = true;

    question.columns.forEach((col, index) => {
        if (!col.attachments?.length && !validateEmptyLabel(col.label, [
            'question',
            question.id,
            `column-${index}`
        ])) {
            valid = false;
        }
    });

    question.rows.forEach((row, index) => {
        if (!row.attachments?.length && !validateEmptyLabel(row.label, [
            'question',
            question.id,
            `row-${index}`
        ])) {
            valid = false;
        }
    });

    return valid;
}

function validateTableScaleQuestion(question: TableScaleQuestion): boolean {
    let valid = true;

    question.rows.forEach((row, index) => {
        if (!row.attachments?.length && !validateEmptyLabel(row.label, [
            'question',
            question.id,
            `row-${index}`
        ])) {
            valid = false;
        }
    });

    return valid;
}

const validateByTypeMap: {[key in QuestionType]?: (question: Question, request: AudienceRequest) => boolean} = {
    'choice': validateChoiceQuestion,
    'sidebyside': validateSideBySideQuestion,
    'email': validateEmailQuestion,
    'table': validateTableChoiceQuestion,
    'tableScale': validateTableScaleQuestion
};

function validateUX(context: UXContext, revision: Revision, request: AudienceRequest): boolean {
    let valid = true;

    if (!validateUXTaskLabelLength(context.task)) valid = false;
    if (!validateEmptyLabel(context.task, [TASK_LABEL_ERROR_KEY])) valid = false;
    if (!validateUXQuestions(context.questions)) valid = false;

    context.questions.forEach(({questions = {}}) => {
        const revisionQuestions = Object.values(questions).map(q => revision.questions[q.id]).filter(Boolean);

        if (validateQuestions(revisionQuestions, request) === false) {
            valid = false;
        }
    });

    const revisionQuestions = Object.values(context.customQuestions?.questions || {})
        .map(q => revision.questions[q.id]).filter(Boolean);

    if (validateQuestions(revisionQuestions, request) === false) {
        valid = false;
    }

    return valid;
};

export const validateByBoxMap: {[key in BoxType]?:
    (context: BoxContext, revision: Revision, request: AudienceRequest) => boolean} = {
    'ux': validateUX
};

export function validateUXTaskLabelLength(taskText: string): boolean {
    if (taskText.length > MAX_TASK_LENGTH) {
        setError(
            TASK_LABEL_ERROR_KEY,
            i18n.error.task
                .replace('%max', MAX_TASK_LENGTH.toString())
                .replace('%length', taskText.length.toString())
        );

        return false;
    }

    unsetError(TASK_LABEL_ERROR_KEY);

    return true;
}

export function validateUXQuestions(questions: UXContext['questions'] = []): boolean {
    if (questions.length) {
        unsetError(TASK_QUESTIONS_ERROR_KEY);

        return true;
    }

    setError(TASK_QUESTIONS_ERROR_KEY, '');

    return false;
}

export function validateLabel(
    label: string,
    maxLength: number | undefined,
    errorPath: (string | number)[]
) {
    if (validateEmptyLabel(label, errorPath)) {
        unsetError(errorPath);
    } else return;

    validateLabelLength(label, maxLength, errorPath);
}

export function validateEmptyLabel(label: string, errorPath: (string | number)[]): boolean {
    if (label.trim().length === 0) {
        setError(errorPath, ValidationErrorType.Empty);

        return false;
    }

    return true;
}

export function validateLabelLength(
    label: string,
    maxLength: number | undefined,
    errorPath: (string | number)[]
) {
    if (!maxLength) {
        return;
    }

    const cleaned = extractCleanText(label);

    if (cleaned.length > maxLength) {
        setError(errorPath, ValidationErrorType.MaxLengthReached);
    }

    if (cleaned.length <= maxLength) {
        unsetError(errorPath);
    }
}

export function setError(path: string | Array<string | number>, value: any) {
    queryClient.setQueryData(ERRORS_KEY, (old: Errors = {}) => {
        const newErrors = cloneDeep(old);

        setWith(newErrors, path, value, Object);

        return newErrors;
    });
}

export function unsetError(path: string | Array<string | number>) {
    queryClient.setQueryData(ERRORS_KEY, (old: Errors = {}) => {
        const newErrors = cloneDeep(old);

        unset(newErrors, path);

        return newErrors;
    });
}

export function scrollToError() {
    scroller.scrollTo(ERROR_CLASS_NAME, {
        duration: 500,
        smooth: true,
        offset: -240
    });
}

export function hasErrorDOMElements() {
    return Boolean(document.querySelector(`.${ERROR_CLASS_NAME}`));
}

export function validateQuestions(questions: Question[], request: AudienceRequest) {
    let valid = true;

    Object.values(questions).forEach(question => {
        if (!validateEmptyLabel(question.label, ['question', question.id, 'label'])) {
            valid = false;
        }

        if (validateByTypeMap[question.type]?.(question, request) === false) {
            valid = false;
        }

        mapSpecialOptions(question, (_, key, option) => {
            if (
                option.enabled &&
                !validateEmptyLabel(option.label, ['question', question.id, key])
            ) {
                valid = false;
            }
        });
    });

    return valid;
}
